import React, { useState } from 'react';
import { Star } from 'lucide-react';

interface StarRatingProps {
  value: number; // 0 to 5 (can be decimal for display)
  onChange?: (rating: number) => void;
  readOnly?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showValue?: boolean;
  reviewCount?: number;
  className?: string;
}

export const StarRating: React.FC<StarRatingProps> = ({
  value,
  onChange,
  readOnly = true,
  size = 'md',
  showValue = false,
  reviewCount,
  className = '',
}) => {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const starSizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-7 h-7',
  };

  const currentRating = hoverValue !== null ? hoverValue : value;

  const handleClick = (rating: number) => {
    if (!readOnly && onChange) {
      onChange(rating);
    }
  };

  return (
    <div className={`inline-flex items-center gap-1.5 ${className}`}>
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((starIndex) => {
          const isFilled = currentRating >= starIndex;
          const isHalf = !isFilled && currentRating > starIndex - 1 && currentRating < starIndex;

          return (
            <button
              key={starIndex}
              type="button"
              disabled={readOnly}
              onClick={() => handleClick(starIndex)}
              onMouseEnter={() => !readOnly && setHoverValue(starIndex)}
              onMouseLeave={() => !readOnly && setHoverValue(null)}
              className={`transition-transform duration-100 ${
                readOnly
                  ? 'cursor-default'
                  : 'cursor-pointer hover:scale-110 focus:outline-hidden'
              } p-0.5`}
              aria-label={`${starIndex} Star${starIndex > 1 ? 's' : ''}`}
            >
              <Star
                className={`${starSizes[size]} ${
                  isFilled
                    ? 'text-amber-400 fill-amber-400'
                    : isHalf
                    ? 'text-amber-400 fill-amber-400/50'
                    : 'text-slate-300 fill-slate-100'
                }`}
              />
            </button>
          );
        })}
      </div>

      {(showValue || reviewCount !== undefined) && (
        <span className="text-xs font-bold text-slate-700 flex items-center gap-1">
          {showValue && <span>{Number(value).toFixed(1)}</span>}
          {reviewCount !== undefined && (
            <span className="text-slate-400 font-normal">({reviewCount})</span>
          )}
        </span>
      )}
    </div>
  );
};
